/*
The single place analytics consent is enforced.

Amplitude, Datadog RUM and Pendo are all "statistics" cookies under Cookiebot.
`startAnalyticsConsentSync` runs once at bootstrap: it starts Datadog when
statistics consent arrives, and tears every tool down again if that consent is
withdrawn. Amplitude's start-up stays in App because it depends on React state,
but it reads the same `statistics` flag and re-checks consent internally.
*/

import { onStatisticsConsentChange } from 'utils/helpers/cookiebot';
import { enableDatadogTracking, disableDatadogTracking } from 'config/datadog.minimal.config';
import { disableAmplitudeTracking } from 'utils/Amplitude';

// Amplitude persists device and session state under these key prefixes.
const AMPLITUDE_STORAGE_PREFIXES = ['amp_', 'AMP_'];

// Datadog's session cookie. stopSession() ends the session but leaves this behind.
const DATADOG_SESSION_COOKIE = '_dd_s';

const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT';

interface PendoAgent {
  stopSendingEvents?: () => void;
}

const clearAmplitudeStorage = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => AMPLITUDE_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable (private mode, blocked): nothing was persisted to clear.
  }
};

/*
Datadog writes _dd_s host-only or scoped to the registrable domain depending on
`trackSessionAcrossSubdomains`, so expire it under both scopes to be sure.
*/
const clearDatadogCookie = (): void => {
  const { hostname } = window.location;
  const registrableDomain = hostname.split('.').slice(-2).join('.');
  const scopes = ['', `;domain=${hostname}`, `;domain=.${registrableDomain}`];

  scopes.forEach((scope) => {
    document.cookie = `${DATADOG_SESSION_COOKIE}=;expires=${EXPIRED};path=/${scope}`;
  });
};

const stopPendo = (): void => {
  const pendo: PendoAgent | undefined = window.pendo;
  pendo?.stopSendingEvents?.();
};

/*
Everything a consent withdrawal has to undo. Safe to call when nothing ever
started: each SDK helper no-ops if it was never initialised.
*/
export const disableStatisticsTracking = (): void => {
  disableDatadogTracking();
  disableAmplitudeTracking();
  stopPendo();
  clearAmplitudeStorage();
  clearDatadogCookie();
};

/*
Call once at bootstrap, before React renders. Returns an unsubscribe function
that module-scope callers can ignore.
*/
export const startAnalyticsConsentSync = (): (() => void) =>
  onStatisticsConsentChange((granted) => {
    if (granted) {
      enableDatadogTracking();
      return;
    }
    disableStatisticsTracking();
  });
